import React from 'react';
import PropTypes from 'prop-types';
import injectSheet from 'react-jss';
import cn from 'classnames';
import {
  responsiveImageType,
  tileDefaultProps,
  tileShape as basicTileShape,
  videoType,
} from '../../propTypes';
import Link from '../Link';
import ImageOrVideo from '../ImageOrVideo';
import theme, {
  cardHover,
  cardTitle,
  easeOutCubic,
  easeOutExpo,
  easeOutQuad,
  easeOutQuint,
  expandAbsolutely,
  sliceColourTheme,
} from '../../styles/theme';
import get from '../../helpers/get';

const Logo = ({
  title,
  classes,
  logo,
  video,
}) => {
  if (!logo && !video) {
    return null;
  }
  const logoSrc = get(logo, ['sizes', 0, 'url']);
  const isSvgLogo = logoSrc && /\.svg.*/.test(logoSrc);
  const alt = (logo && logo.alt) || title || '';
  return (
    <ImageOrVideo
      aspect={1}
      className={classes.logo}
      imageClassName={cn(isSvgLogo && classes.svgLogo)}
      image={logo}
      video={video}
      alt={alt}
    />
  );
};

Logo.propTypes = {
  title: PropTypes.string,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  logo: responsiveImageType,
  video: videoType,
};

Logo.defaultProps = {
  logo: undefined,
  video: undefined,
  title: undefined,
};

const Tile = ({
  classes,
  className,
  clickthrough,
  image,
  video,
  logo,
  logoVideo,
  title,
  dropShadow,
}) => (
  <Link
    className={
      cn(
        className,
        classes.tile,
        image && classes.withHoverImage,
        !title && classes.minimal,
        !dropShadow && classes.titleNoShadow,
      )
    }
    link={clickthrough}
  >
    <div className={classes.imageWrapper}>
      <Logo title={title} classes={classes} logo={logo} video={logoVideo} />
      {(image || video) && (
        <ImageOrVideo
          aspect={1}
          className={classes.image}
          image={image}
          video={video}
          alt={(image && image.alt) || title || ''}
        />
      )}
    </div>
    {title && (
      <h3 className={classes.title}>
        {title}
      </h3>
    )}
  </Link>
);

export const tileShape = {
  ...basicTileShape,
  logo: responsiveImageType,
};

Tile.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  className: PropTypes.string,
  logoVideo: videoType,
  dropShadow: PropTypes.bool,
  backgroundColour: PropTypes.string,
  ...tileShape,
};

Tile.defaultProps = {
  className: undefined,
  logo: undefined,
  logoVideo: undefined,
  dropShadow: true,
  backgroundColour: sliceColourTheme.grey.backgroundColour,
  ...tileDefaultProps,
};

export default injectSheet({
  tile: {
    display: 'block',
    margin: [0, 0, theme.spacing.vertical.sm / 2],
    '&, .cms-content &[href]': {
      textDecoration: 'none',
    },
    '&:focus, &:hover': {
      '& $image': {
        opacity: 1,
        transform: 'scale(1.2)',
        transition: `opacity 0.4s ${easeOutQuint}`,
        animation: `zoomup 0.65s ${easeOutExpo}`,
      },
    },
    'a&': {
      ...cardHover('$imageWrapper', false),
    },
  },
  titleNoShadow: {
    'a& $imageWrapper, a&:focus $imageWrapper, a&:hover $imageWrapper, a&:active $imageWrapper': {
      boxShadow: 'none',
    },
  },
  imageWrapper: {
    position: 'relative',
    paddingTop: '100%',
    marginBottom: theme.padding.gutter / 2,
    backgroundColor: ({ backgroundColour }) => backgroundColour,
    overflow: 'hidden',
  },
  logo: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    left: 0,
    top: 0,
    '& img': {
      objectFit: 'contain',
      fontFamily: '"object-fit: contain;"', // object-fit polyfill
    },
  },
  svgLogo: {
    width: '50%',
    height: '50%',
    left: '25%',
    top: '25%',
    margin: 0,
  },
  image: {
    ...expandAbsolutely,
    opacity: 0,
    transition: `opacity 0.3s ${easeOutCubic}`,
    animation: `zoomaway 0.3s ${easeOutQuad}`,
  },
  minimal: {
    marginBottom: theme.padding.gutter,
    '& $imageWrapper': {
      marginBottom: 0,
    },
  },
  title: {
    ...cardTitle,
    marginBottom: 0,
    [theme.breakpoints.between(320, 'sm')]: {
      fontSize: 16,
    },
    [theme.breakpoints.between(0, 320)]: {
      fontSize: ({ isStackedOnMobile }) => (!isStackedOnMobile ? 16 : null),
    },
  },
  '@keyframes zoomup': {
    '0%': {
      transform: 'scale(1)',
    },
    '100%': {
      transform: 'scale(1.2)',
    },
  },
  '@keyframes zoomaway': {
    '0%': {
      transform: 'scale(1.2)',
    },
    '100%': {
      transform: 'scale(1.6)',
    },
  },
})(Tile);
